// @ts-check

import { useState } from 'react';
import { ChevronRight, ChevronDown, Copy, Check } from 'lucide-react';

/**
 * @typedef {Object} ThemeConfig
 * @property {string} keyColor - Tailwind class for object keys
 * @property {string} stringColor - Tailwind class for string values
 * @property {string} numberColor - Tailwind class for number values
 * @property {string} booleanColor - Tailwind class for boolean values
 * @property {string} nullColor - Tailwind class for null values
 * @property {string} objectColor - Tailwind class for object/array labels
 * @property {string} hoverBg - Tailwind class for hover background
 * @property {string} searchHighlight - Tailwind class for search highlights
 * @property {string} borderColor - Tailwind class for tree borders
 */

/**
 * @typedef {Object} CopyCallbackData
 * @property {any} value - The copied value
 * @property {Array<string|number>} path - Path to the copied value in the tree
 * @property {string} stringValue - JSON stringified version of the value
 */

/**
 * JsonTreeView - A reusable component for displaying JSON data as an expandable tree
 * 
 * @param {Object} props
 * @param {any} props.data - The data to display (can be any JSON-serializable value)
 * @param {string} [props.searchTerm] - Optional search term to highlight matches
 * @param {number} [props.defaultExpandDepth] - How many levels to expand by default (default: 1)
 * @param {boolean} [props.showCopyButtons] - Whether to show copy buttons (default: true)
 * @param {boolean} [props.showDataTypes] - Whether to show data type hints (default: true)
 * @param {Partial<ThemeConfig>} [props.theme] - Color theme customization
 * @param {((data: CopyCallbackData) => void)|null} [props.onCopy] - Callback when data is copied
 * @param {string} [props.className] - Additional CSS classes for the container
 */
function JsonTreeView({
  data,
  searchTerm = '',
  defaultExpandDepth = 1,
  showCopyButtons = true,
  showDataTypes = true,
  theme = {},
  onCopy = null,
  className = ''
}) {
  const defaultTheme = {
    keyColor: 'text-purple-600 dark:text-purple-400',
    stringColor: 'text-green-600 dark:text-green-400',
    numberColor: 'text-blue-600 dark:text-blue-400',
    booleanColor: 'text-orange-600 dark:text-orange-400',
    nullColor: 'text-gray-500',
    objectColor: 'text-gray-600 dark:text-gray-400',
    hoverBg: 'hover:bg-muted/50',
    searchHighlight: 'bg-accent',
    borderColor: 'border-border',
    ...theme
  };

  return (
    <div className={`font-mono text-sm ${className}`}>
      <TreeNode
        data={data}
        path={[]}
        searchTerm={searchTerm.toLowerCase()}
        defaultExpandDepth={defaultExpandDepth}
        currentDepth={0}
        showCopyButtons={showCopyButtons}
        showDataTypes={showDataTypes}
        theme={defaultTheme}
        onCopy={onCopy}
      />
    </div>
  );
}

/**
 * @param {Object} props
 * @param {any} props.data - The data to display in this node
 * @param {Array<string|number>} props.path - Current path in the tree
 * @param {string} props.searchTerm - Search term for highlighting
 * @param {number} props.defaultExpandDepth - How many levels to expand by default
 * @param {number} props.currentDepth - Current depth level in the tree
 * @param {string|number|null} [props.keyName] - The key name for this node
 * @param {boolean} props.showCopyButtons - Whether to show copy buttons
 * @param {boolean} props.showDataTypes - Whether to show data type hints
 * @param {ThemeConfig} props.theme - Theme configuration object
 * @param {((data: CopyCallbackData) => void)|null} props.onCopy - Callback when data is copied
 */
function TreeNode({
  data,
  path,
  searchTerm,
  defaultExpandDepth,
  currentDepth,
  keyName = null,
  showCopyButtons,
  showDataTypes,
  theme,
  onCopy
}) {
  const [isExpanded, setIsExpanded] = useState(currentDepth < defaultExpandDepth);
  const [copied, setCopied] = useState(false);

  const toggleExpand = () => setIsExpanded(!isExpanded);

  /**
   * Copy the value to clipboard
   * @param {any} value - The value to copy
   */
  const copyToClipboard = async (value) => {
    const stringValue = JSON.stringify(value, null, 2);
    await navigator.clipboard.writeText(stringValue);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);

    if (onCopy) {
      onCopy({ value, path: [...path, keyName].filter(Boolean), stringValue });
    }
  };

  // Handle primitive values
  if (data === null || typeof data !== 'object') {
    const stringValue = JSON.stringify(data);
    const matches = searchTerm && stringValue.toLowerCase().includes(searchTerm);

    return (
      <div className={`flex items-start gap-2 py-0.5 group ${matches ? theme.searchHighlight : ''}`}>
        {keyName !== null && (
          <>
            <span className={theme.keyColor}>{keyName}</span>
            <span className="text-muted-foreground/50">:</span>
          </>
        )}
        <span className={getValueColor(data, theme)}>{stringValue}</span>
        {showDataTypes && (
          <span className="text-xs text-muted-foreground/60 italic">
            {typeof data}
          </span>
        )}
        {showCopyButtons && (
          <button
            onClick={() => copyToClipboard(data)}
            className="opacity-0 group-hover:opacity-100 transition-all p-1 bg-transparent border-0"
            title="Copy value"
          >
            {copied ? <Check className="w-3 h-3 text-chart-2" /> : <Copy className="w-3 h-3 text-muted-foreground hover:text-primary" />}
          </button>
        )}
      </div>
    );
  }

  // Handle objects and arrays
  const isArray = Array.isArray(data);
  const entries = isArray ? data.map((v, i) => [i, v]) : Object.entries(data);
  const isEmpty = entries.length === 0;

  const preview = isArray
    ? isEmpty ? '[]' : `Array(${data.length})`
    : isEmpty ? '{}' : `Object(${entries.length})`;

  const matchesSearch = searchTerm && JSON.stringify(data).toLowerCase().includes(searchTerm);

  return (
    <div className={`${matchesSearch ? 'bg-accent/30' : ''}`}>
      <div className={`group flex items-center gap-1 py-0.5 ${theme.hoverBg}`}>
        {!isEmpty && (
          <button
            onClick={toggleExpand}
            className="p-0.5 transition-colors bg-transparent border-0"
            title={isExpanded ? 'Collapse' : 'Expand'}
          >
            {isExpanded ? (
              <ChevronDown className="w-4 h-4 text-muted-foreground hover:text-primary transition-colors" />
            ) : (
              <ChevronRight className="w-4 h-4 text-muted-foreground hover:text-primary transition-colors" />
            )}
          </button>
        )}
        {isEmpty && <div className="w-5" />}

        {keyName !== null && (
          <>
            <span className={theme.keyColor}>{keyName}</span>
            <span className="text-muted-foreground/50">:</span>
          </>
        )}

        <span className={`${theme.objectColor} font-semibold`}>{preview}</span>

        {showDataTypes && !isEmpty && (
          <span className="text-xs text-muted-foreground/60 italic">
            {isArray ? 'array' : 'object'}
          </span>
        )}

        {showCopyButtons && (
          <button
            onClick={() => copyToClipboard(data)}
            className="opacity-0 group-hover:opacity-100 transition-all p-1 bg-transparent border-0"
            title="Copy object"
          >
            {copied ? <Check className="w-3 h-3 text-chart-2" /> : <Copy className="w-3 h-3 text-muted-foreground hover:text-primary" />}
          </button>
        )}
      </div>

      {isExpanded && !isEmpty && (
        <div className={`ml-6 border-l-2 ${theme.borderColor} pl-2`}>
          {entries.map(([key, value]) => (
            <TreeNode
              key={key}
              data={value}
              path={[...path, keyName].filter(Boolean)}
              searchTerm={searchTerm}
              defaultExpandDepth={defaultExpandDepth}
              currentDepth={currentDepth + 1}
              keyName={key}
              showCopyButtons={showCopyButtons}
              showDataTypes={showDataTypes}
              theme={theme}
              onCopy={onCopy}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Get the color class for a value based on its type
 * @param {any} value - The value to get the color for
 * @param {ThemeConfig} theme - The theme configuration object
 * @returns {string} The color class for the value
 */
function getValueColor(value, theme) {
  if (value === null) return theme.nullColor;
  if (typeof value === 'string') return theme.stringColor;
  if (typeof value === 'number') return theme.numberColor;
  if (typeof value === 'boolean') return theme.booleanColor;
  return 'text-foreground';
}

export default JsonTreeView
